import type { Session, User } from '@halo/chat-sdk-react';
import { Box, Menu } from '@chakra-ui/react';
import { useCallback, useState, useMemo, useRef, memo } from 'react';
import { SessionCategoryPrefix, isSessionCategory, useAppStore } from '@halo/chat-sdk-react';
import { useNavigate } from 'react-router';
import { useStore } from 'zustand';
import { useShallowEnhance } from '@halo/zustand-enhance';
import Paths from '@/configs/router/paths';

interface AvatarBoxProps {
  session: Session;
  user: User | undefined;
  fetchProfileUser: () => void;
  children: React.ReactNode;
}

export const AvatarMenu = memo(
  ({ session, user, fetchProfileUser, children }: AvatarBoxProps) => {
    const { t } = useTranslation();
    const {
      createSingleSession,
      user: currentUser,
      sessionStores,
    } = useAppStore(useShallowEnhance(['createSingleSession', 'user', 'sessionStores']));
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const isClickTriggered = useRef<boolean>(false);
    const isGroup = useMemo(
      () => isSessionCategory(session?.chat_id, SessionCategoryPrefix.Group),
      [session?.chat_id]
    );

    const groupAccess = useStore(sessionStores[session.chat_id], (store) => store.groupAccess);
    const isMe = useMemo(() => {
      return user?.user_id === currentUser?.user_id;
    }, [user?.user_id, currentUser?.user_id]);

    const { canPrivateMessage } = useMemo(() => {
      return {
        canPrivateMessage: groupAccess?.canPrivateMessage,
      };
    }, [groupAccess?.canPrivateMessage]);

    const items = useMemo(
      () =>
        [
          {
            value: 'profile',
            label: <Box fontWeight="semibold">{t('chat.openProfile')}</Box>,
            onItemClick: () => {
              fetchProfileUser();
              setMenuOpen(false);
            },
          },
          {
            value: 'send_message',
            label: <Box fontWeight="semibold">{t('chat.sendMessage')}</Box>,
            hidden: !canPrivateMessage,
            onItemClick: async () => {
              const session = await createSingleSession(user!.user_id);
              navigate(Paths.MESSAGES_CHATS + '/' + session?.chat_id);
              setMenuOpen(false);
            },
          },
          // {
          //   value: 'mute',
          //   label: <Box fontWeight="semibold">Mute</Box>,
          //   onItemClick: () => {
          //     setMenuOpen(false);
          //   },
          // },
        ].filter((item) => !item.hidden),
      [fetchProfileUser, user, createSingleSession, navigate, canPrivateMessage, t]
    );

    const handleAvatarClick = useCallback(() => {
      if (isGroup && !isMe) {
        isClickTriggered.current = true;
        setMenuOpen(true);
      } else {
        fetchProfileUser();
      }
    }, [isGroup, fetchProfileUser, isMe]);

    const handleOpenChange = useCallback(({ open }: { open: boolean }) => {
      if (open && !isClickTriggered.current) {
        return;
      }
      if (open) {
        isClickTriggered.current = false;
      }
      setMenuOpen(open);
    }, []);

    if (!isGroup || isMe) {
      return (
        <Box onClick={handleAvatarClick} alignSelf="flex-start">
          {children}
        </Box>
      );
    }

    return (
      <Menu.Root lazyMount open={menuOpen} onOpenChange={handleOpenChange}>
        <Menu.Trigger asChild>
          <Box height="fit-content" onClick={handleAvatarClick}>
            {children}
          </Box>
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content
            boxShadow="0px 0px 18px 0px rgba(0, 0, 0, 0.1)"
            rounded="lg"
            minW="180px"
            p="1"
            backdropFilter="blur(6px)"
            bg="bg"
          >
            {items.map((item: any) => {
              if (item.hidden) return null;
              return (
                <Menu.Item
                  key={item.value}
                  {...item}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onItemClick?.();
                  }}
                  px="1"
                  py="3"
                  borderRadius="sm"
                  cursor="pointer"
                  color={item.danger ? 'fg.error' : 'fg'}
                  _hover={{ bg: item.danger ? 'bg.error' : 'bg.subtle' }}
                >
                  {item.label}
                </Menu.Item>
              );
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    );
  }
);

AvatarMenu.displayName = 'AvatarMenu';
